import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useAccount, usePublicClient, useWaitForTransactionReceipt, useWalletClient } from 'wagmi';
//import { parseEther } from 'viem';

import CardBaseFC, { RoundNotLoadedYet } from 'components/CardBase/index.js';
import { ToastErrorTx, ToastProgressTx, ToastSuccessTx } from 'utils/Toasts/transaction';
import { FormattedPrice } from 'utils/Price';
import { ReactComponent as DogHouse } from 'assets/icons/dogehouse.svg';
import { ReactComponent as Crosshair } from 'assets/icons/Crosshair.svg';
import BearWon from 'assets/art/DOGE_BETS_BEARISH.webp';
import BullWon from 'assets/art/DOGE_BETS_CIRCLE_BULLISH.webp';
import { ABI, REWARD_PRECISION_DECIMALS } from 'const';
import CardPreviousBackside from './backside';
import { useGameDetails, useHouseFee, usePreviousRound, useUserBetForRoundID } from 'utils/Hooks';
import { GetPredictionContractAddr } from 'context/url_based_context';
import './styles.scss';

const ROUND_STATUSES = {
  bull: 0,
  bear: 1,
  tie: 2,
  canceled: 3,
  error: 4,
};

function CardPrevious(props) {
  const backsideTimeout = useRef(null);
  const gameDetails = useGameDetails();
  const rewardDecimalsToShow = REWARD_PRECISION_DECIMALS[gameDetails.wantedChain.name];

  const walletClient = useWalletClient({ chainId: gameDetails.wantedChain.id });
  const publicClient = usePublicClient();
  const account = useAccount();

  const round = usePreviousRound();
  const houseFee = useHouseFee();
  const userBetData = useUserBetForRoundID(round.epoch);

  const [roundStatus, SetRoundStatus] = useState(undefined);
  const [isClaimable, SetWhetherRoundClaimable] = useState(false);
  const [isRefundable, SetWhetherRoundRefundable] = useState(false);
  const [showingBackside, SetWhetherShowingCardBackside] = useState(false);

  const [betTxHash, SetBetTxHash] = useState(null);
  const [formattedError, SetFormattedError] = useState('');


  const {
    isSuccess: isTxConfirmed_hook, failureReason: txFailReason
  } = useWaitForTransactionReceipt({ hash: betTxHash, confirmations: 1 });


  useEffect(() => {
    DetermineTxStatusFromHooks()
  }, [isTxConfirmed_hook, betTxHash, txFailReason, isClaimable]);

  useEffect(() => {
    HandleTxError();
  }, [formattedError, betTxHash]);


  function DetermineTxStatusFromHooks() {
    if (isTxConfirmed_hook && betTxHash) {
      console.info(`TX CONFIRMED by hook`);
      ShowTxSuccessToast(betTxHash);
      if (isClaimable) { // only show backside for won rounds
        ShowBackside();
      }
    } else if (txFailReason) {
      console.info(`TX REVERTED according to hook`);
      let reason = 'unknown error';
      const matches = txFailReason.details.match(/:(.*): 0x/);
      if (matches && matches[1])
        reason = matches[1].trim();
      else
        reason = txFailReason.shortMessage.slice(0, 40);
      SetFormattedError(`${reason}`);
    }
  }

  function ShowTxPendingToast() {
    ToastProgressTx();
  }

  function HandleTxError() {
    if (formattedError) {
      ToastErrorTx(formattedError, betTxHash);
      SetBetTxHash(null); // stop useWaitForTransactionReceipt hook from further updates
      SetFormattedError('');
    }
  }

  function ShowTxSuccessToast(_txHash) {
    if (!_txHash)
      throw new SyntaxError(`missing transaction hash when trying to show success toast`);
    ToastSuccessTx(_txHash);
    SetBetTxHash(null); // stop useWaitForTransactionReceipt hook from further updates
  }


  useEffect(() => {
    return () => {
      if (backsideTimeout.current) clearTimeout(backsideTimeout.current);
    }
  }, [backsideTimeout]);

  useEffect(() => {
    UpdateRoundClaimability();
    const refresher = setInterval(UpdateRoundClaimability, 5000);
    return () => {
      clearInterval(refresher);
    }
  }, [round, gameDetails, account?.address])

  useEffect(() => {
    UpdateRoundStatus();
  }, [round, roundStatus]);


  function GetAmountWon() {
    if (RoundNotLoadedYet(round) || !userBetData) return null;
    if (Number(round.rewardBaseCalAmount) === 0) return 0;
    const rewardRate = round.rewardAmount / round.rewardBaseCalAmount;
    const reward = (userBetData.amount * rewardRate) / 10 ** 18;
    const rewardTruncated = Math.floor(reward * 10 ** rewardDecimalsToShow) / 10 ** rewardDecimalsToShow;
    return rewardTruncated.toFixed(rewardDecimalsToShow);
  }

  async function UpdateRoundClaimability() {
    if (RoundNotLoadedYet(round) || !account?.address) {
      SetWhetherRoundClaimable(false);
      SetWhetherRoundRefundable(false);
    } else {
      const contract = gameDetails.contracts.prediction;
      const claimable = await contract.methods.Claimable(round.epoch, account?.address).call();
      const refundable = await contract.methods.Refundable(round.epoch, account?.address).call();
      SetWhetherRoundClaimable(claimable);
      SetWhetherRoundRefundable(refundable);
    }
  }

  function UpdateRoundStatus() {
    if (RoundNotLoadedYet(round)) return;
    let status;
    const lockPrice = Number(round.lockPrice);
    const closePrice = Number(round.closePrice);
    if (round.canceled)
      status = ROUND_STATUSES.canceled;
    else if (!round.closed)
      status = ROUND_STATUSES.error;
    else if (lockPrice > closePrice)
      status = ROUND_STATUSES.bear;
    else if (lockPrice < closePrice)
      status = ROUND_STATUSES.bull;
    else
      status = ROUND_STATUSES.tie;

    if (status !== roundStatus) {
      SetRoundStatus(status);
    }
  }

  function ShowBackside() {
    SetWhetherShowingCardBackside(true);
    backsideTimeout.current = setTimeout(() => SetWhetherShowingCardBackside(false), 10000);
  }

  function ShowDisclaimer() {
    alert('A ' + houseFee + '% house fee is included with all claimed winnings.\n'
      + 'There is no fee for a tie or a loss.');
  }

  function Payout() {
    let output;
    let payout;
    if (RoundNotLoadedYet(round)) {
      output = null;
    } else {
      const rewards = Number(round.bullAmount) + Number(round.bearAmount);
      switch (roundStatus) {
        case ROUND_STATUSES.bull:
          if (Number(round.bullAmount) === 0)
            payout = 0;
          else
            payout = (rewards / Number(round.bullAmount)).toFixed(2) + 'x';
          break;
        case ROUND_STATUSES.bear:
          if (Number(round.bearAmount) === 0)
            payout = 0;
          else
            payout = (rewards / Number(round.bearAmount)).toFixed(2) + 'x';
          break;
        case ROUND_STATUSES.tie:
          payout = '1.00x';
          break;
        case ROUND_STATUSES.canceled:
          payout = '1.00x';
          break;
        case ROUND_STATUSES.error:
          payout = '1.00x';
          break;
        default:
          payout = 'Pending...';
          break;
      }
      output = (
        <p className='Payout'>
          Payout: <span>{payout}</span>
        </p>
      );
    }
    return output;
  }

  function LockPrice() {
    let output;
    if (RoundNotLoadedYet(round)) {
      output = null;
    } else {
      const price = (Number(round.lockPrice) / 10 ** 8);
      output = (
        <div className='Price Locked'>
          <p className='Label'>Start</p>
          <p className='Figure'>
            $ <FormattedPrice price={price} />
          </p>
        </div>
      );
    }
    return output;
  }

  function ClosePrice() {
    let output;
    if (RoundNotLoadedYet(round)) {
      output = null;
    } else {
      const price = (Number(round.closePrice) / 10 ** 8);
      output = (
        <div className='Price Closed'>
          <p className='Label'>End</p>
          <p className='Figure'>
            $ <FormattedPrice price={price} />
          </p>
        </div>
      );
    }
    return output;
  }

  function PriceDifference() {
    let output, color;
    if (RoundNotLoadedYet(round)) {
      output = null;
    } else {
      const price = ((Number(round.closePrice) - Number(round.lockPrice)) / 10 ** 8)
      if (price > 0)
        color = 'var(--color-bull-green)';
      else if (price < 0)
        color = 'var(--color-bear-red)';
      else
        color = 'var(--color-theme-white)';

      output = (
        <p className='Difference' style={{ color: color }}>
          $<FormattedPrice price={price} />
        </p>
      );
    }
    return output;
  }

  function AssetPrice() {
    return (
      <div className='ThemeBox FlexCenter PriceOfPredictionAsset'>
        {LockPrice()}
        {ClosePrice()}
        <div className='Separator' />
        {PriceDifference()}
        {VerifyButton()}
      </div>
    );
  }

  function RoundStatus() {
    let status;
    switch (roundStatus) {
      case ROUND_STATUSES.bull:
        status = (<img src={BullWon} alt='Bull Won' />);
        break;
      case ROUND_STATUSES.bear:
        status = (<img src={BearWon} alt='Bear Won' />);
        break;
      case ROUND_STATUSES.tie:
        status = (<div className='ThemeBox RoundResult'>Tied</div>);
        break;
      case ROUND_STATUSES.canceled:
        status = (<div className='ThemeBox RoundResult'>Refunded</div>);
        break;
      case ROUND_STATUSES.error:
        status = (<div className='ThemeBox RoundResult'>Error</div>);
        break;
      default:
        status = (<div className='ThemeBox RoundResult'>Pending...</div>);
        break;
    }
    return <div>{status}</div>;
  }


  async function Claim() {
    if (RoundNotLoadedYet(round) || !account?.address) return;
    let simulationResult = null;
    try {
      simulationResult = await publicClient.simulateContract({
        address: GetPredictionContractAddr(gameDetails),
        abi: ABI.prediction,
        functionName: 'user_Claim',
        args: [[round.epoch]],
        account: account.address,
        value: 0n,
      });
    } catch (e) {
      if (e?.shortMessage) {
        ToastErrorTx(e.shortMessage);
      }
      return;
    }

    let txHash = '';
    ShowTxPendingToast();
    try {
      txHash = await walletClient.data.writeContract(simulationResult.request);
      SetBetTxHash(txHash);
    } catch (e) {
      if (e?.shortMessage) {
        SetFormattedError(e.shortMessage);
      } else {
        SetFormattedError(String(e).slice(0, 40));
      }
      SetBetTxHash(null);
      return;
    }
  }


  function ClaimButton() {
    let output;
    if (isClaimable)
      output = (<button className='ThemeButton Gold' onClick={Claim}>
        Claim
      </button>);
    else if (isRefundable)
      output = (<button className='ThemeButton Gold ' onClick={Claim}>
        Refund
      </button>);
    else
      output = null;

    return output;
  }

  function VerifyButton() {
    const roundDetailsURL = GetDetailsURL();
    return (<Link className='VerifyButton' to={roundDetailsURL}>
      <Crosshair className='CrosshairIcon' />
      Verify<span className='Exclamation'>!</span>
    </Link>);
  }
  function GetDetailsURL() {
    return ('/history/' + gameDetails.chainName.toLowerCase() + '/'
      + gameDetails.tickerSymbol.toLowerCase() + `/round/${round.epoch}?showHistData`);
  }


  function Disclaimer() {
    if (roundStatus !== ROUND_STATUSES.bull && roundStatus !== ROUND_STATUSES.bear)
      return null;
    else
      return (<div className='Disclaimer'>
        <button onClick={ShowDisclaimer}>
          <DogHouse />
        </button>
        (<b>{houseFee}%</b> Dogehouse Fee)
      </div>);
  }



  if (showingBackside)
    return (<CardPreviousBackside
      bearVictory={(roundStatus === ROUND_STATUSES.bear) ? true : false}
      roundNumber={round.epoch}
      amountWon={GetAmountWon()}
    //amountWon={0.1241} // debug
    />);
  else
    return (<CardBaseFC
      cardName='Last'
      round={round}
    >
      <div className='Status'>
        {RoundStatus()}
      </div>

      {AssetPrice()}

      <div className='ThemeBox FlexCenter'>
        {Payout()}
        {Disclaimer()}
      </div>
      {ClaimButton()}
    </CardBaseFC>);
}

export default CardPrevious;
